/**
 * AdminPaymentsPage - aprovar / rejeitar pagamentos
 * Melhorias: exportação CSV
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Eye, Check, X, Loader2, Download } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/shared/StatusBadge";
import { fmtDateTime } from "@/lib/date";

export default function AdminPaymentsPage() {
    const [tab, setTab] = useState("pendente");
    const [items, setItems] = useState([]);
    const [sel, setSel] = useState(null);
    const [note, setNote] = useState("");
    const [working, setWorking] = useState(false);

    const load = async () => {
        const { data } = await api.get(`/admin/payments?status=${tab}`);
        setItems(data);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [tab]);

    const exportCsv = () => {
        const rows = [
            ["Cliente", "Email", "Empresa", "Plano", "Método", "Moeda", "Valor AOA", "Valor USD", "Estado", "Referência", "Data"],
            ...items.map((p) => [
                p.user_name || "",
                p.user_email || "",
                p.user_company || "",
                p.plan_id || "",
                p.method === "multicaixa_express" ? "Multicaixa Express"
                    : p.method === "e_kwanza" ? "É-Kwanza"
                    : p.method === "referencia_mc" ? "Referência MC"
                    : "Transferência",
                p.currency || "",
                p.amount_aoa || "",
                p.amount_usd || "",
                p.status || "",
                p.reference || "",
                p.created_at ? new Date(p.created_at).toLocaleDateString("pt-PT") : "",
            ]),
        ];
        const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mesafa-pagamentos-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exportado");
    };

    const review = async (decision) => {
        if (!sel) return;
        setWorking(true);
        try {
            await api.post(`/admin/payments/${sel.id}/review`, { decision, note: note || null });
            toast.success(decision === "aprovado" ? "Pagamento aprovado e subscrição ativada" : "Pagamento rejeitado");
            setSel(null);
            setNote("");
            load();
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setWorking(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-payments-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Pagamentos</h1>
                    <p className="text-muted-foreground">Validar comprovativos e aprovar subscrições.</p>
                </div>
                <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="admin-payments-export-csv">
                    <Download className="h-4 w-4" /> Exportar CSV
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="pendente" data-testid="admin-payments-tab-pending">Pendentes</TabsTrigger>
                    <TabsTrigger value="aprovado" data-testid="admin-payments-tab-approved">Aprovados</TabsTrigger>
                    <TabsTrigger value="rejeitado" data-testid="admin-payments-tab-rejected">Rejeitados</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-4">
                    <Card className="glass-card p-0 rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-secondary/40 text-xs uppercase">
                                    <tr>
                                        <th className="text-left p-3">Cliente</th>
                                        <th className="text-left p-3 hidden md:table-cell">Plano / Método</th>
                                        <th className="text-left p-3">Valor</th>
                                        <th className="text-left p-3 hidden md:table-cell">Data</th>
                                        <th className="text-right p-3">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((p) => (
                                        <tr key={p.id} className="border-t border-white/5" data-testid={`payment-row-${p.id}`}>
                                            <td className="p-3">
                                                <div className="font-medium">{p.user_name}</div>
                                                <div className="text-xs text-muted-foreground">{p.user_email}</div>
                                            </td>
                                            <td className="p-3 hidden md:table-cell">
                                                <div>{p.plan_id}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {p.method === "multicaixa_express" ? "Multicaixa Express"
                                                        : p.method === "e_kwanza" ? "É-Kwanza"
                                                        : p.method === "referencia_mc" ? "Referência MC"
                                                        : "Transferência"}
                                                </div>
                                            </td>
                                            <td className="p-3 font-medium">
                                                {p.currency === "USD" ? `${p.amount_usd} USD` : `${p.amount_aoa.toLocaleString("pt-PT")} AOA`}
                                            </td>
                                            <td className="p-3 hidden md:table-cell text-xs text-muted-foreground">
                                                {fmtDateTime(p.created_at)}
                                            </td>
                                            <td className="p-3 text-right">
                                                <Button size="sm" variant="outline" onClick={() => setSel(p)} data-testid={`admin-payment-view-${p.id}`}>
                                                    <Eye className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {items.length === 0 && (
                                        <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Sem registos.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Pagamento</DialogTitle>
                        <DialogDescription>{sel?.user_name} · {sel?.user_email}</DialogDescription>
                    </DialogHeader>
                    {sel && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <Card className="p-3 rounded-xl bg-secondary/40">
                                    <div className="text-xs text-muted-foreground">Valor</div>
                                    <div className="font-display text-xl font-semibold">
                                        {sel.currency === "USD" ? `${sel.amount_usd} USD` : `${sel.amount_aoa.toLocaleString("pt-PT")} AOA`}
                                    </div>
                                </Card>
                                <Card className="p-3 rounded-xl bg-secondary/40">
                                    <div className="text-xs text-muted-foreground">Plano · Método</div>
                                    <div className="font-medium">{sel.plan_id}</div>
                                    <div className="text-xs">{
                                        sel.method === "multicaixa_express" ? "Multicaixa Express"
                                        : sel.method === "e_kwanza" ? "É-Kwanza"
                                        : sel.method === "referencia_mc" ? "Referência Multicaixa"
                                        : "Transferência"
                                    }</div>
                                </Card>
                            </div>
                            {sel.reference && (
                                <div className="text-sm"><span className="text-muted-foreground">Referência:</span> {sel.reference}</div>
                            )}
                            <div>
                                <Label>Comprovativo</Label>
                                {sel.proof_url?.startsWith("data:image") ? (
                                    <img src={sel.proof_url} alt="comprovativo" className="mt-1 rounded-xl max-h-96 object-contain w-full bg-black/40" />
                                ) : sel.proof_url?.startsWith("data:application/pdf") ? (
                                    <a href={sel.proof_url} target="_blank" rel="noreferrer" className="text-[#A78BFA] underline text-sm">
                                        Abrir PDF · {sel.proof_filename}
                                    </a>
                                ) : (
                                    <div className="text-sm text-muted-foreground">Comprovativo não visível.</div>
                                )}
                            </div>
                            {sel.status === "pendente" && (
                                <div>
                                    <Label>Nota (opcional)</Label>
                                    <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-payment-note-input" />
                                </div>
                            )}
                            {sel.admin_note && sel.status !== "pendente" && (
                                <Card className="p-3 rounded-xl bg-secondary/40 text-sm">
                                    <span className="text-muted-foreground">Nota admin:</span> {sel.admin_note}
                                </Card>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSel(null)}>Fechar</Button>
                        {sel?.status === "pendente" && (
                            <>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="outline" className="border-[#FB7185]/40 text-[#FB7185]" data-testid="admin-reject-payment-button">
                                            <X className="h-4 w-4 mr-2" /> Rejeitar
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Rejeitar pagamento?</AlertDialogTitle>
                                            <AlertDialogDescription>O cliente será informado e a subscrição não será ativada.</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => review("rejeitado")}>Confirmar</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                                <Button onClick={() => review("aprovado")} disabled={working} className="btn-grad-purple text-white" data-testid="admin-approve-payment-button">
                                    {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                                    Aprovar e ativar
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
